import { defineStore } from 'pinia';
import { type Component, isRef, markRaw, type Ref, type ComputedRef } from 'vue';

type Hex = `#${string}`;
type DynStr = string | Ref<string> | ComputedRef<string> | (() => string);

export type HeaderAction = {
	icon?: () => string;
	btnClass?: () => string;
	onClick?: () => void;
	id?: string;
	component?: Component;
};

export type HeaderActionHandle = {
	remove: () => void;
	update?: (next: Partial<{ icon: DynStr; btnClass: DynStr; onClick: () => void; id: string }>) => void;
};

function toGetter(v: DynStr): () => string {
	if (typeof v === 'function') return () => (v as () => string)();
	if (isRef(v)) return () => v.value;
	return () => v;
}

export const useAppOptionsStore = defineStore({
	id: 'appOption',
	state: () => ({
		title: globalThis.config?.['app.name'] || globalThis.env?.app?.name || 'EF Vue SPA App',
		title_short: globalThis.config?.['app.short_name'],
		backgroundUrlExtern: globalThis.config?.['app.background_url_extern'] ?? '/img/login-background.jpg',

		is_minimized: false,

		appTheme: 'light' as 'light' | 'dark' | 'auto',
		appThemeDividers: false,
		appThemeGradients: true,
		appThemeShadows: false,

		appPrimaryColor: globalThis.config?.['app.primary_color'] ?? '#0d6efd',
		appSecondaryColor: globalThis.config?.['app.secondary_color'] ?? '#6c757d',

		showApplications: globalThis.config?.['app.show_applications'] ?? '1',

		appSidebarCollapsed: false,
		appSidebarMinified: false,
		appSidebarSearch: false,
		appFooter: true,

		is_locked: false,

		search: undefined as
			| undefined
			| {
					callback?: (s: string) => boolean;
					component?: Component;
					displayBreakpoint?: string;
			  },

		headerActions: [] as HeaderAction[],

		routerKey: Date.now().toString(),

		// We allow the override of Layout Components, e.g. the Context Menu to visualise App specific data
		contextHeaderSlot: null as null | Component,
	}),
	actions: {
		refreshRouterView() {
			this.routerKey = Date.now().toString();
		},
		toggleIsMinimized() {
			this.is_minimized = !this.is_minimized;
		},
		addHeaderAction(opts: {
			icon?: DynStr;
			btnClass?: DynStr;
			onClick?: () => void;
			id?: string;
			component?: Component;
		}): HeaderActionHandle {
			const { icon, btnClass, onClick, id, component } = opts;
			// If an action with same onClick or id exists, reuse that one and still return a handle for it
			let existing = this.headerActions.find((a) => (onClick && a.onClick === onClick) || (id && a.id === id));
			if (!existing) {
				existing = {
					icon: icon ? toGetter(icon) : undefined,
					btnClass: btnClass ? toGetter(btnClass) : undefined,
					onClick,
					id,
					component: component ? markRaw(component) : undefined,
				};
				this.headerActions.push(existing);
			}

			const remove = () => {
				const idx = this.headerActions.indexOf(existing!);
				if (idx !== -1) this.headerActions.splice(idx, 1);
			};

			const update = (next: Partial<{ icon: DynStr; btnClass: DynStr; onClick: () => void; id: string }>) => {
				if (next.icon !== undefined) existing!.icon = toGetter(next.icon);
				if (next.btnClass !== undefined) existing!.btnClass = toGetter(next.btnClass);
				if (next.onClick !== undefined) existing!.onClick = next.onClick;
				if (next.id !== undefined) existing!.id = next.id;
			};

			return { remove, update };
		},
		clearHeaderActions() {
			this.headerActions = [] as HeaderAction[];
		},
		/*init() {
			this.title = window.config?.['app.name'] || 'FetSy 2.0';
			this.title_short = window.config?.['app.name.short'] || this.title;
			this.loginBackgroundUrl = window.config?.['app.login.background'] || '/img/login-background.jpg';
			this.registerBackgroundUrl = window.config?.['app.register.background'] || '/img/login-background.jpg';
		},*/
	},
});

export function hexToRgbList(hex: string) {
	const h = hex.replace('#', '');
	const [r, g, b] =
		h.length === 3 ?
			[...h].map((c) => parseInt(c + c, 16))
		:	[h.slice(0, 2), h.slice(2, 4), h.slice(4, 6)].map((c) => parseInt(c, 16));
	return `${r}, ${g}, ${b}`;
}

function applyBootstrapStylesToDOM(primary: string, secondary: string) {
	if (typeof document === 'undefined') return;
	const root = document.documentElement;
	if (primary) {
		root.style.setProperty('--bs-primary', primary);
		root.style.setProperty('--bs-primary-rgb', hexToRgbList(primary));
	}
	if (secondary) {
		root.style.setProperty('--bs-secondary', secondary);
		root.style.setProperty('--bs-secondary-rgb', hexToRgbList(secondary));
	}
}
